import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMount } from 'react-use'
import styled, { css } from 'styled-components'

import { CircularLoader, SearchInput } from '@blade/ui-kit'
import { loadDefaultCurrenciesAction } from '@root/features/currency/default-currencies'
import { FadeOverflow, InfoMessage, mediaMinTablet } from '@root/shared/ui-kit'

import { reatomWalletAssetsManagement } from '../../atoms/reatom-assets-management'

import { WalletAssetToggleItem } from './wallet-asset-toggle-item'

import type { WalletModel } from '@root/entities/wallet/atoms'
import type { ClassNameProps } from '@root/shared/react'

type AssetsManagementBodyProps = {
  wallet: WalletModel
} & ClassNameProps

export const AssetsManagementBody: React.FC<AssetsManagementBodyProps> = ({
  wallet,
  className,
}) => {
  const { t } = useTranslation()
  const model = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomWalletAssetsManagement({ walletId: wallet.id }),
    [wallet.id],
  )
  const [currencies] = useAtom(model.currenciesAtom)
  const [searchState] = useAtom(model.stateAtom)
  const [searchQuery] = useAtom(model.searchQueryAtom)

  const setQuery = useAction(model.setQueryAction)
  const loadDefaultCurrencies = useAction(loadDefaultCurrenciesAction)

  useMount(() => loadDefaultCurrencies())

  const renderResults = () => {
    if (searchState.loading) {
      return (
        <LoadingContainer>
          {t('common.searching')}
          <CircularLoader size={18} />
        </LoadingContainer>
      )
    }

    if (searchQuery.trim() && currencies.length === 0) {
      return <InfoMessage intent="empty" />
    }

    return (
      <ScrollContent
        mode="always"
        variant="vertical"
        blockEnd={16}
        blockStart={16}
      >
        <List>
          {currencies.map((it) => (
            <WalletAssetToggleItem
              key={it.address}
              currency={it}
              wallet={wallet}
            />
          ))}
        </List>
      </ScrollContent>
    )
  }

  return (
    <Root className={className}>
      <Top>
        {/* TODO: Ivan - check sizes xs vs h32 */}
        <Input
          size="h32"
          placeholder={t('currency.search-token')}
          value={searchQuery}
          onChange={(event) => setQuery(event.target.value)}
        />
      </Top>
      {renderResults()}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 10px;
`

const ScrollContent = styled(FadeOverflow)`
  overflow: auto;
`

const Input = styled(SearchInput)`
  margin-top: 16px;

  ${mediaMinTablet(css`
    height: 32px;
    padding: 0 8px;
    border-radius: 8px;
    font-size: 12px;
    gap: 4px;
  `)}
`

const Top = styled.div`
  padding-inline: 12px;
  display: grid;
`

const List = styled.div`
  padding-inline: 12px;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding-top: 16px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  font-size: 12px;
`
